import React, { useState, useEffect } from 'react';
import supplierService from '../services/supplierService';
import { IcBuilding, IcMail, IcPhone, IcMapPin, IcContact, IcLandmark, IcSave, IcPlus, IcTrash, IcClose } from '../theme/icons';
import { BtnSk } from './Skeleton';

// Panneau « Nouveau fournisseur » — même modale que « Nouveau laboratoire »
// (LaboratoireList) : en-tête modal-head, corps défilant, pied Annuler/Enregistrer.
// Reprend les 4 blocs du formulaire AddSupplier (généralités, téléphones,
// légal, bancaire) pour ne plus quitter la liste des fournisseurs.
// Formulaire (Brand Kit §05) : champ .inp 42px bordé #D1D5DB rayon 6,
// 13.5px, padding 11, focus 2px encre ; libellé 12px 500 encre, mb 4.
const inputClass = 'inp w-full px-3.5 py-2.5 rounded-xl text-base text-gray-900';
const labelClass = 'block text-sm font-medium text-gray-900 mb-1.5';

const emptyForm = {
  name: '',
  email: '',
  address: '',
  registre_commercial: '',
  matricule_fiscale: '',
  rib: '',
  bank_name: '',
};

const SupplierFormModal = ({ open, onClose, onCreated }) => {
  const [formData, setFormData] = useState(emptyForm);
  // Plusieurs numéros — liste à la saisie, chaîne séparée par des virgules à l'envoi.
  const [phones, setPhones] = useState(['']);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Formulaire vierge à chaque ouverture.
  useEffect(() => {
    if (open) {
      setFormData(emptyForm);
      setPhones(['']);
      setError(null);
      setSaving(false);
    }
  }, [open]);

  // Échap ferme le panneau (sauf pendant l'enregistrement).
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !saving) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, saving, onClose]);

  if (!open) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePhoneChange = (index, value) => {
    setPhones((prev) => prev.map((p, i) => (i === index ? value : p)));
  };

  const addPhone = () => setPhones((prev) => [...prev, '']);

  const removePhone = (index) => {
    setPhones((prev) => (prev.length === 1 ? [''] : prev.filter((_, i) => i !== index)));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setError('Le nom du fournisseur est requis.');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      const phone = phones.map((p) => p.trim()).filter(Boolean).join(', ');
      const created = await supplierService.createSupplier({ ...formData, name: formData.name.trim(), phone });
      if (onCreated) await onCreated(created);
      onClose();
    } catch (err) {
      console.error('Error creating supplier:', err);
      let message = err && err.message;
      if (!message && err && typeof err === 'object') {
        const first = Object.values(err)[0];
        message = Array.isArray(first) ? first[0] : first;
      }
      setError(message || 'Une erreur est survenue lors de la création du fournisseur.');
      setSaving(false);
    }
  };

  const closeIfIdle = () => {
    if (!saving) onClose();
  };

  return (
    <div className="modal-backdrop flex items-center justify-center z-50 p-4" onClick={closeIfIdle}>
      <div
        className="bg-white rounded-2xl shadow-lg w-full max-w-2xl max-h-[calc(100vh-2rem)] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="supplier-modal-title"
      >
        <div className="modal-head p-5">
          <h3 id="supplier-modal-title" className="modal-title text-lg">Nouveau fournisseur</h3>
          <button type="button" onClick={closeIfIdle} className="text-gray-400 hover:text-white text-xl" aria-label="Fermer"><IcClose /></button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col min-h-0 flex-1">
          <div className="p-6 space-y-6 overflow-y-auto min-h-0 flex-1">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl" role="alert">{error}</div>
            )}

            {/* ===== Informations générales ===== */}
            <section>
              <h4 className="text-sm font-bold text-gray-700 mb-4 flex items-center gap-2">
                <span className="h-7 w-7 rounded-lg bg-slate-100 text-slate-500 flex items-center justify-center"><IcBuilding className="text-xs" /></span>
                Informations générales
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className={labelClass} htmlFor="sup-name">Nom <span className="req">*</span></label>
                  <input type="text" id="sup-name" name="name" value={formData.name} onChange={handleChange}
                    className={inputClass} placeholder="Nom du fournisseur" required autoFocus />
                </div>
                <div>
                  <label className={labelClass} htmlFor="sup-email">Email</label>
                  <div className="relative">
                    <IcMail className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                    <input type="email" id="sup-email" name="email" value={formData.email} onChange={handleChange}
                      className={`${inputClass} pl-10`} placeholder="Email du fournisseur" />
                  </div>
                </div>
                <div>
                  <label className={labelClass} htmlFor="sup-address">Adresse</label>
                  <div className="relative">
                    <IcMapPin className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                    <input type="text" id="sup-address" name="address" value={formData.address} onChange={handleChange}
                      className={`${inputClass} pl-10`} placeholder="Adresse du fournisseur" />
                  </div>
                </div>
              </div>
            </section>

            {/* ===== Téléphones ===== */}
            <section>
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-sm font-bold text-gray-700 flex items-center gap-2">
                  <span className="h-7 w-7 rounded-lg bg-gray-100 text-gray-500 flex items-center justify-center"><IcPhone className="text-xs" /></span>
                  Numéros de téléphone
                </h4>
                <button type="button" onClick={addPhone} className="btn-add-link gap-1.5 text-sm font-medium">
                  <IcPlus className="text-xs" /> Ajouter un numéro
                </button>
              </div>
              <div className="space-y-3">
                {phones.map((phone, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <div className="relative flex-1">
                      <IcPhone className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                      <input
                        type="tel"
                        value={phone}
                        onChange={(e) => handlePhoneChange(index, e.target.value)}
                        className={`${inputClass} pl-10`}
                        placeholder={index === 0 ? 'Numéro principal' : `Numéro ${index + 1}`}
                        aria-label={index === 0 ? 'Numéro principal' : `Numéro ${index + 1}`}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => removePhone(index)}
                      disabled={phones.length === 1 && !phones[0]}
                      className={`h-10 w-10 shrink-0 flex items-center justify-center rounded-xl transition-colors ${
                        phones.length === 1 && !phones[0] ? 'text-gray-300 cursor-not-allowed' : 'text-red-600 hover:bg-red-50'
                      }`}
                      title="Supprimer ce numéro"
                    >
                      <IcTrash className="text-sm" />
                    </button>
                  </div>
                ))}
              </div>
            </section>

            {/* ===== Informations légales ===== */}
            <section>
              <h4 className="text-sm font-bold text-gray-700 mb-4 flex items-center gap-2">
                <span className="h-7 w-7 rounded-lg bg-gray-100 text-gray-500 flex items-center justify-center"><IcContact className="text-xs" /></span>
                Informations légales
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass} htmlFor="sup-rc">Registre Commercial</label>
                  <input type="text" id="sup-rc" name="registre_commercial" value={formData.registre_commercial}
                    onChange={handleChange} className={inputClass} placeholder="Registre commercial" />
                </div>
                <div>
                  <label className={labelClass} htmlFor="sup-mf">Matricule Fiscale</label>
                  <input type="text" id="sup-mf" name="matricule_fiscale" value={formData.matricule_fiscale}
                    onChange={handleChange} className={inputClass} placeholder="Matricule fiscale" />
                </div>
              </div>
            </section>

            {/* ===== Informations bancaires ===== */}
            <section>
              <h4 className="text-sm font-bold text-gray-700 mb-4 flex items-center gap-2">
                <span className="h-7 w-7 rounded-lg bg-gray-100 text-gray-500 flex items-center justify-center"><IcLandmark className="text-xs" /></span>
                Informations bancaires
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass} htmlFor="sup-rib">RIB</label>
                  <input type="text" id="sup-rib" name="rib" value={formData.rib} onChange={handleChange}
                    className={`${inputClass} font-mono tracking-wide`} placeholder="RIB" />
                </div>
                <div>
                  <label className={labelClass} htmlFor="sup-bank">Nom de la banque</label>
                  <input type="text" id="sup-bank" name="bank_name" value={formData.bank_name} onChange={handleChange}
                    className={inputClass} placeholder="Nom de la banque" />
                </div>
              </div>
            </section>
          </div>

          {/* Pied de modale (Brand Kit §05) : 11/16px, filet gray-100, gap 8 */}
          <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-100">
            <button type="button" onClick={closeIfIdle} className="px-5 py-2.5 btn-g transition-colors">
              Annuler
            </button>
            <button type="submit" disabled={saving} className="flex items-center py-2.5 px-6 btn-p transition-colors">
              {saving ? (
                <>
                  <BtnSk />
                  Enregistrement...
                </>
              ) : (
                <><IcSave className="mr-2" /> Enregistrer</>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SupplierFormModal;
